import { Action } from '@ngrx/store'
import {
  RAP_EV_TOW_LIMITATION_FLEX,
  RAP_TOW_LIMITATION_FLEX,
} from '../../../shared/utils/messages'
import { TowLimitRestriction } from '../../advisories/advisories.types'
import { VendorConfigurationMode } from '../../auth/mode-configuration.types'
import { ClubMembershipTowing } from '../../servicing-club/servicing-club.types'
import events from '../../tagging/events'
import { openMessageDialog, openPromptDialog } from '../../ui/ui.actions'
import { MessageDialogTypes, PromptDialogTypes } from '../../ui/ui.types'
import { TOW_DISTANCE, TOWING_ALERT_TYPES } from '../location.types'

export const DRR_TOW_DISTANCE_LIMIT_DEFAULT = (limit: number) => $localize`The tow location you have selected is more than ${limit} miles away. Please verify your towing destination. If the address is accurate and you\'d like to proceed with this destination, please give us a call.`

export type TowBenefitsOutcome = 'hardBlock' | 'softWarning' | 'pass'

export interface TowBenefitsResult {
  outcome: TowBenefitsOutcome
  dialog?: Action
  taggingEvent?: string
}

export interface TowBenefitsInput {
  isRapUser: boolean
  hasTowSettings: boolean
  memberLevel: ClubMembershipTowing | null
  distance: number
  towDestinationDistance: number | null
  boundaryMileage: number | null
  eligibilityTowLimit: number
  modeConfiguration: Pick<VendorConfigurationMode, 'callCenterTowMileage'>
  shownAars: ReadonlyArray<{ id?: string | number; distanceTo?: number }>
  destinationId?: string | number
  isEvStation: boolean
}

const PASS: TowBenefitsResult = { outcome: 'pass' }

const evaluateRap = (input: TowBenefitsInput): TowBenefitsResult => {
  const { towDestinationDistance, boundaryMileage, eligibilityTowLimit, modeConfiguration, shownAars, destinationId, isEvStation } = input
  const towDistanceFinal = towDestinationDistance ? towDestinationDistance : boundaryMileage
  const isOverLimit = towDistanceFinal > (eligibilityTowLimit || modeConfiguration.callCenterTowMileage)
  const isDestinationAvailableWithinLimit = Boolean(
    shownAars.find((aar) => aar.id !== destinationId && aar.distanceTo <= eligibilityTowLimit)
  )
  const isManualInput = !destinationId
  const isNotAllowedAtAll = towDistanceFinal > modeConfiguration.callCenterTowMileage || (isManualInput && isOverLimit)

  if (isNotAllowedAtAll) {
    return {
      outcome: 'hardBlock',
      dialog: openMessageDialog({ payload: { type: MessageDialogTypes.RAP_TOW_MILEAGE_MESSAGE } }),
    }
  }
  if (isOverLimit && isDestinationAvailableWithinLimit) {
    return {
      outcome: 'softWarning',
      dialog: openMessageDialog({
        payload: {
          type: MessageDialogTypes.TOW_BOUNDARY_MESSAGE,
          params: { message: isEvStation ? RAP_EV_TOW_LIMITATION_FLEX() : RAP_TOW_LIMITATION_FLEX() },
        },
      }),
    }
  }
  return PASS
}

const evaluateCustom = (input: TowBenefitsInput): TowBenefitsResult => {
  const { boundaryMileage, distance, memberLevel } = input
  if (!(boundaryMileage < distance)) {
    return PASS
  }
  return {
    outcome: 'softWarning',
    dialog: openMessageDialog({
      payload: {
        type: MessageDialogTypes.TOW_BOUNDARY_MESSAGE,
        params: {
          message: memberLevel.message || DRR_TOW_DISTANCE_LIMIT_DEFAULT(boundaryMileage),
          overMileage: Math.ceil(distance - boundaryMileage),
          distance: Math.ceil(distance),
          limit: boundaryMileage,
        },
      },
    }),
  }
}

const evaluateDefault = ({ distance }: TowBenefitsInput): TowBenefitsResult => {
  if (TOW_DISTANCE.LIMIT < distance) {
    return {
      outcome: 'hardBlock',
      dialog: openMessageDialog({
        payload: {
          type: MessageDialogTypes.TOWING_DISTANCE_LIMIT,
          title: $localize`Alert`,
          content: DRR_TOW_DISTANCE_LIMIT_DEFAULT(TOW_DISTANCE.LIMIT),
          params: {
            overMileage: Math.ceil(distance - TOW_DISTANCE.LIMIT),
            distance: Math.ceil(distance),
            limit: TOW_DISTANCE.LIMIT,
          },
        },
      }),
      taggingEvent: events.towTo.DESTINATION_DISTANCE_ALERT,
    }
  }
  if (TOW_DISTANCE.WARNING < distance) {
    return {
      outcome: 'softWarning',
      dialog: openPromptDialog({
        payload: {
          type: PromptDialogTypes.TOWING_DISTANCE_WARN,
          title: $localize`Alert`,
          content: $localize`The tow location you have entered is greater than ${TOW_DISTANCE.WARNING} miles away. Do you wish to proceed?`,
        },
      }),
      taggingEvent: events.towTo.DESTINATION_DISTANCE_WARNING,
    }
  }
  return PASS
}

// The tow-benefits rules (RAP / club member-level / default), classified as a
// hard block, a soft warning or a pass. Pure: the caller dispatches the dialog.
// `towLimit.maxMiles` never enters this evaluation.
export const evaluateTowBenefits = (input: TowBenefitsInput): TowBenefitsResult => {
  const type = input.isRapUser
    ? TOWING_ALERT_TYPES.RAP
    : input.hasTowSettings && input.memberLevel
      ? TOWING_ALERT_TYPES.CUSTOM
      : TOWING_ALERT_TYPES.DEFAULT

  switch (type) {
    case TOWING_ALERT_TYPES.RAP:
      return evaluateRap(input)
    case TOWING_ALERT_TYPES.CUSTOM:
      return evaluateCustom(input)
    default:
      return evaluateDefault(input)
  }
}

// A towLimit blocks only when the destination is over maxMiles and the club has
// not deferred to per-destination availability (enabled=false).
export const isTowLimitHardBlock = (
  towLimitRestriction: TowLimitRestriction | null,
  distance: number
): boolean =>
  !!towLimitRestriction && distance > towLimitRestriction.maxMiles && !towLimitRestriction.enabled
